import { useFrame, useThree } from "@react-three/fiber";
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { createBodyPart, getTexture, createShadowDisc } from "../../../utils/steve3D";
import {
  PLAYER_ARMS_ROTATION,
  PLAYER_DISTANCE,
  PLAYER_OFFSET,
  PLAYER_OFFSET_X,
  PLAYER_SCALE,
} from "../constants";

export default function SteveBody({ cursorPosition, texture }) {
  const playerRef = useRef();
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(0, 0, PLAYER_DISTANCE);
    camera.lookAt(0, 0, 0);

    const player = new THREE.Group();
    player.add(createShadowDisc());

    const is64x64 = texture.image && texture.image.height >= 64;

    // Body (Base)
    player.add(
      createBodyPart(
        8 / 64,
        12 / 64,
        4 / 64,
        [
          getTexture(texture, 16, 20, 4, 12), // Right
          getTexture(texture, 28, 20, 4, 12), // Left
          getTexture(texture, 20, 16, 8, 4), // Top
          getTexture(texture, 28, 16, 8, 4), // Bottom
          getTexture(texture, 20, 20, 8, 12), // Front
          getTexture(texture, 32, 20, 8, 12), // Back
        ],
        0,
        18 / 64,
        0
      )
    );

    // Body (Jacket / Outer Layer)
    if (is64x64) {
      player.add(
        createBodyPart(
          8.5 / 64,
          12.5 / 64,
          4.5 / 64,
          [
            getTexture(texture, 16, 36, 4, 12, true), // Right
            getTexture(texture, 28, 36, 4, 12, true), // Left
            getTexture(texture, 20, 32, 8, 4, true), // Top
            getTexture(texture, 28, 32, 8, 4, true), // Bottom
            getTexture(texture, 20, 36, 8, 12, true), // Front
            getTexture(texture, 32, 36, 8, 12, true), // Back
          ],
          0,
          18 / 64,
          0
        )
      );
    }

    // Left Arm (Base)
    const leftArm = createBodyPart(
      4 / 64,
      12 / 64,
      4 / 64,
      [
        getTexture(texture, 32, 52, 4, 12), // Right
        getTexture(texture, 40, 52, 4, 12), // Left
        getTexture(texture, 36, 48, 4, 4), // Top
        getTexture(texture, 40, 48, 4, 4), // Bottom
        getTexture(texture, 36, 52, 4, 12), // Front
        getTexture(texture, 44, 52, 4, 12), // Back
      ],
      6.4 / 64,
      18.4 / 64,
      0
    );
    leftArm.rotation.z = PLAYER_ARMS_ROTATION;
    player.add(leftArm);

    // Left Arm (Sleeve / Outer Layer)
    if (is64x64) {
      const leftSleeve = createBodyPart(
        4.5 / 64,
        12.5 / 64,
        4.5 / 64,
        [
          getTexture(texture, 48, 52, 4, 12, true), // Right
          getTexture(texture, 56, 52, 4, 12, true), // Left
          getTexture(texture, 52, 48, 4, 4, true), // Top
          getTexture(texture, 56, 48, 4, 4, true), // Bottom
          getTexture(texture, 52, 52, 4, 12, true), // Front
          getTexture(texture, 60, 52, 4, 12, true), // Back
        ],
        6.4 / 64,
        18.4 / 64,
        0
      );
      leftSleeve.rotation.z = PLAYER_ARMS_ROTATION;
      player.add(leftSleeve);
    }

    // Right Arm (Base)
    const rightArm = createBodyPart(
      4 / 64,
      12 / 64,
      4 / 64,
      [
        getTexture(texture, 40, 20, 4, 12), // Right
        getTexture(texture, 48, 20, 4, 12), // Left
        getTexture(texture, 44, 16, 4, 4), // Top
        getTexture(texture, 48, 16, 4, 4), // Bottom
        getTexture(texture, 44, 20, 4, 12), // Front
        getTexture(texture, 52, 20, 4, 12), // Back
      ],
      -6.4 / 64,
      18.4 / 64,
      0
    );
    rightArm.rotation.z = -PLAYER_ARMS_ROTATION;
    player.add(rightArm);

    // Right Arm (Sleeve / Outer Layer)
    if (is64x64) {
      const rightSleeve = createBodyPart(
        4.5 / 64,
        12.5 / 64,
        4.5 / 64,
        [
          getTexture(texture, 40, 36, 4, 12, true), // Right
          getTexture(texture, 48, 36, 4, 12, true), // Left
          getTexture(texture, 44, 32, 4, 4, true), // Top
          getTexture(texture, 48, 32, 4, 4, true), // Bottom
          getTexture(texture, 44, 36, 4, 12, true), // Front
          getTexture(texture, 52, 36, 4, 12, true), // Back
        ],
        -6.4 / 64,
        18.4 / 64,
        0
      );
      rightSleeve.rotation.z = -PLAYER_ARMS_ROTATION;
      player.add(rightSleeve);
    }

    // Left Leg (Base)
    player.add(
      createBodyPart(
        4 / 64,
        12 / 64,
        4 / 64,
        [
          getTexture(texture, 16, 52, 4, 12), // Right
          getTexture(texture, 24, 52, 4, 12), // Left
          getTexture(texture, 20, 48, 4, 4), // Top
          getTexture(texture, 24, 48, 4, 4), // Bottom
          getTexture(texture, 20, 52, 4, 12), // Front
          getTexture(texture, 28, 52, 4, 12), // Back
        ],
        2 / 64,
        6 / 64,
        0
      )
    );

    // Left Leg (Pants / Outer Layer)
    if (is64x64) {
      player.add(
        createBodyPart(
          4.5 / 64,
          12.5 / 64,
          4.5 / 64,
          [
            getTexture(texture, 0, 52, 4, 12, true), // Right
            getTexture(texture, 8, 52, 4, 12, true), // Left
            getTexture(texture, 4, 48, 4, 4, true), // Top
            getTexture(texture, 8, 48, 4, 4, true), // Bottom
            getTexture(texture, 4, 52, 4, 12, true), // Front
            getTexture(texture, 12, 52, 4, 12, true), // Back
          ],
          2 / 64,
          6 / 64,
          0
        )
      );
    }

    // Right Leg (Base)
    player.add(
      createBodyPart(
        4 / 64,
        12 / 64,
        4 / 64,
        [
          getTexture(texture, 0, 20, 4, 12), // Right
          getTexture(texture, 8, 20, 4, 12), // Left
          getTexture(texture, 4, 16, 4, 4), // Top
          getTexture(texture, 8, 16, 4, 4), // Bottom
          getTexture(texture, 4, 20, 4, 12), // Front
          getTexture(texture, 12, 20, 4, 12), // Back
        ],
        -2 / 64,
        6 / 64,
        0
      )
    );

    // Right Leg (Pants / Outer Layer)
    if (is64x64) {
      player.add(
        createBodyPart(
          4.5 / 64,
          12.5 / 64,
          4.5 / 64,
          [
            getTexture(texture, 0, 36, 4, 12, true), // Right
            getTexture(texture, 8, 36, 4, 12, true), // Left
            getTexture(texture, 4, 32, 4, 4, true), // Top
            getTexture(texture, 8, 32, 4, 4, true), // Bottom
            getTexture(texture, 4, 36, 4, 12, true), // Front
            getTexture(texture, 12, 36, 4, 12, true), // Back
          ],
          -2 / 64,
          6 / 64,
          0
        )
      );
    }

    const playerContainer = playerRef.current;
    playerContainer.add(player);

    // Scale the entire player
    player.scale.setScalar(PLAYER_SCALE);
    player.translateY(PLAYER_OFFSET);
    player.position.x = PLAYER_OFFSET_X;

    return () => {
      playerContainer.remove(player);
      player.traverse((object) => {
        if (!object.isMesh) return;

        object.geometry.dispose();
        const materials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        materials.forEach((material) => {
          const map = material.map;
          const lightMap = material.lightMap;
          map?.dispose();
          if (lightMap && lightMap !== map) lightMap.dispose();
          material.dispose();
        });
      });
    };
  }, [camera, texture]);

  useFrame(() => {
    if (playerRef.current) {
      const { x, y } = cursorPosition;
      const angleY = Math.atan2(x, PLAYER_DISTANCE);
      const angleX = Math.atan2(y, PLAYER_DISTANCE);
      playerRef.current.rotation.y = angleY;
      playerRef.current.rotation.x = -angleX;
    }
  });

  return <group ref={playerRef} />;
}
